import { z } from 'zod';
import { EventName } from '../ingest/common.ts';
import { AudienceDefinition } from './audience.ts';
import { CardWithdrawReason, CardWithdrawSelector } from './card-delivery.ts';
import { TemplateSource } from './content-templates.ts';
import { DELIVERY_CHANNELS } from './delivery.ts';
import { MessageExemptions, QuietHours } from './delivery-controls.ts';
import { TriggerSpec } from './triggers.ts';

/**
 * Config schemas of the Canvas steps that ship a runtime handler (BB-115 Message, BB-116 Delay, BB-117 Action Paths,
 * BB-118 Audience Paths) and their ports: one source for the runtime (`@bb/domain` handlers), design-time validation
 * and the dashboard's step panels.
 */

// Message (BB-115)
const Policy = z.enum(['advance', 'exit']);

export const MessageStepConfig = z
  .strictObject({
    channels: z.array(z.enum(DELIVERY_CHANNELS)).min(1),
    /** Channel content as in a campaign variant (push, card, …). */
    content: z.partialRecord(z.enum(DELIVERY_CHANNELS), z.record(z.string(), z.unknown())),
    /** The template version a channel's content was copied from (BB-56). */
    sourceTemplates: z.partialRecord(z.enum(DELIVERY_CHANNELS), TemplateSource).optional(),
    /** Checked right before the send: the Canvas audience, or one of its own. */
    validateAudienceAtSend: z.union([z.literal('canvas_audience'), AudienceDefinition]).optional(),
    onValidationFailure: Policy.default('exit'),
    onSuppressed: Policy.default('advance'),
    onFailed: Policy.default('advance'),
    overrides: z.strictObject({ quietHours: QuietHours.optional(), exemptions: MessageExemptions.optional() }).optional(),
  })
  .refine((c) => c.channels.every((ch) => c.content[ch]), { message: 'every channel needs its content', path: ['content'] });
export type MessageStepConfig = z.infer<typeof MessageStepConfig>;

/** The only way out of a Message step. */
export const MESSAGE_NEXT = 'next';

// Delay (BB-116)
/** The longest delay (Braze: 730 days); a personalized wake-up further away fails personalization. */
export const MAX_DELAY_SECONDS = 730 * 86_400;

const Time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM, 24-hour');
const IsoDate = z.iso.date();
/** Wall-clock times read in the profile's timezone (environment timezone when the profile has none) or the environment's. */
const LocalTime = z.boolean().default(true);
const OnPast = z.enum(['exit', 'advance_now']).default('exit');

const Duration = z.strictObject({ kind: z.literal('duration'), seconds: z.number().int().min(1).max(MAX_DELAY_SECONDS) });
const UntilDate = z.strictObject({ kind: z.literal('until_date'), date: IsoDate, time: Time.default('00:00'), localTime: LocalTime, onPast: OnPast });
const DayOfWeek = z.strictObject({
  kind: z.literal('day_of_week'),
  /** ISO weekdays, 1 = Monday … 7 = Sunday. */
  weekdays: z.array(z.number().int().min(1).max(7)).min(1).max(7),
  time: Time,
  localTime: LocalTime,
  /** Entered on a chosen day after its time: go on now, or wait for the next chosen day (Braze asks the same). */
  lateOnChosenDay: z.enum(['next_occurrence', 'advance_now']).default('next_occurrence'),
});
const Personalized = z.strictObject({
  kind: z.literal('personalized'),
  /** Where the wake-up time comes from: an entry context variable (BB-119) or a profile custom attribute. */
  source: z.strictObject({ from: z.enum(['context', 'attribute']), key: z.string().min(1).max(255) }),
  /** Overrides the time of day of the value (and is the time of a date-only value; midnight otherwise). */
  time: Time.optional(),
  /** Before or after the value, e.g. one day before a trial ends. */
  offset: z.strictObject({ amount: z.number().int().min(0).max(MAX_DELAY_SECONDS), unit: z.enum(['minutes', 'hours', 'days', 'weeks']), direction: z.enum(['before', 'after']) }).optional(),
  localTime: LocalTime,
  /** A value that is missing, not a date, or more than 730 days away (Braze: Personalization Failed). */
  onInvalid: z.enum(['exit', 'advance']).default('exit'),
  onPast: OnPast,
});

/**
 * Delay step settings (BB-116, CAN-004). A config without `kind` is a duration — the shape design-time graphs and
 * templates used before the handler shipped (`{ seconds }`).
 */
export const DelayConfig = z.preprocess(
  (value) => (typeof value === 'object' && value !== null && !Array.isArray(value) && !('kind' in value) ? { kind: 'duration', ...value } : value),
  z.discriminatedUnion('kind', [Duration, UntilDate, DayOfWeek, Personalized]),
);
export type DelayConfig = z.infer<typeof DelayConfig>;

/** The only way out of a Delay step. */
export const DELAY_NEXT = 'next';

// Paths (BB-117, BB-118)
export const AUDIENCE_PATHS_MAX_GROUPS = 8;
/** Port of the profiles no group took; always the last branch. */
export const EVERYONE_ELSE = 'everyone_else';

/** Id of a paths group (Audience Paths, Action Paths): also its port. */
export const PathGroupId = z
  .string()
  .regex(/^[a-z0-9][a-z0-9_-]{0,39}$/, 'group ids are lower-case letters, digits, - and _')
  .refine((id) => id !== EVERYONE_ELSE, `${EVERYONE_ELSE} is reserved`);

export const AudiencePathsConfig = z
  .strictObject({
    groups: z
      .array(z.strictObject({ id: PathGroupId, name: z.string().trim().min(1).max(100), audience: AudienceDefinition, exitAfter: z.boolean().optional() }))
      .min(1)
      .max(AUDIENCE_PATHS_MAX_GROUPS),
    everyoneElse: z.strictObject({ exitAfter: z.boolean().optional() }).default({}),
  })
  .refine((c) => new Set(c.groups.map((g) => g.id)).size === c.groups.length, { message: 'group ids must be unique', path: ['groups'] });
export type AudiencePathsConfig = z.infer<typeof AudiencePathsConfig>;

export const ACTION_PATHS_MAX_GROUPS = 8;
/** Braze: up to 31 days. */
export const ACTION_PATHS_MAX_WINDOW_SECONDS = 31 * 86_400;
/** Events received this long after the window still count when they happened inside it (late SDK flushes). */
export const ACTION_PATHS_GRACE_SECONDS = 60;
export const ActionPathsConfig = z
  .strictObject({
    windowSeconds: z.number().int().min(1).max(ACTION_PATHS_MAX_WINDOW_SECONDS).default(86_400),
    /** Ranking (everyone held to the end of the window, the highest group performed wins) is not built yet. */
    ranking: z
      .boolean()
      .default(false)
      .refine((on) => !on, 'ranking is not available yet: the first group performed takes the user'),
    groups: z
      .array(z.strictObject({ id: PathGroupId, name: z.string().trim().min(1).max(100), triggers: z.array(TriggerSpec).min(1).max(10), exitAfter: z.boolean().optional() }))
      .min(1)
      .max(ACTION_PATHS_MAX_GROUPS),
    everyoneElse: z.strictObject({ exitAfter: z.boolean().optional() }).default({}),
  })
  .refine((c) => new Set(c.groups.map((g) => g.id)).size === c.groups.length, { message: 'group ids must be unique', path: ['groups'] });
export type ActionPathsConfig = z.infer<typeof ActionPathsConfig>;

/** Ports of a paths step: groups that do not end the Canvas, then Everyone Else unless it does. */
export function pathsPorts(config: { groups: ReadonlyArray<{ id: string; exitAfter?: boolean | undefined }>; everyoneElse: { exitAfter?: boolean | undefined } }): string[] {
  return [...config.groups.filter((g) => !g.exitAfter).map((g) => g.id), ...(config.everyoneElse.exitAfter ? [] : [EVERYONE_ELSE])];
}

// Context (BB-119)
export const CONTEXT_VARIABLE_TYPES = ['boolean', 'number', 'string', 'array', 'time', 'object'] as const;
export const CONTEXT_MAX_VARIABLES = 10;
/** Variable names as Braze allows them: letters, digits and `_`, up to 100. */
export const ContextVariableName = z.string().regex(/^[A-Za-z0-9_]{1,100}$/, 'letters, digits and _ (up to 100)');

/**
 * Context step settings: up to 10 variables, each a Liquid expression rendered in order (a later one may use an earlier
 * one through `context.*`) and converted to its type; the entry keeps them as `context.<name>` for every later step.
 */
export const ContextStepConfig = z
  .strictObject({
    variables: z
      .array(z.strictObject({ name: ContextVariableName, type: z.enum(CONTEXT_VARIABLE_TYPES), expression: z.string().min(1).max(10_000) }))
      .min(1)
      .max(CONTEXT_MAX_VARIABLES),
  })
  .refine((c) => new Set(c.variables.map((v) => v.name)).size === c.variables.length, { message: 'variable names must be unique', path: ['variables'] });
export type ContextStepConfig = z.infer<typeof ContextStepConfig>;

/** The only way out of a Context step. */
export const CONTEXT_NEXT = 'next';

// Decision Split (BB-125)
/** Yes / no by one audience, checked when the user reaches the step (Audience Paths with one group). */
export const DecisionSplitConfig = z.strictObject({ audience: AudienceDefinition });
export type DecisionSplitConfig = z.infer<typeof DecisionSplitConfig>;
export const DECISION_SPLIT_PORTS = ['yes', 'no'] as const;

// User Update (BB-120)
export const USER_UPDATE_MAX_ACTIONS = 10;
/** Braze: the Advanced JSON Editor takes up to 65 536 characters; one action's Liquid stays within it. */
export const USER_UPDATE_MAX_EXPRESSION = 65_536;
/** Updates of User Update steps per minute and environment unless `settings.canvasUserUpdatesPerMinute` says otherwise (Braze: 200 000). */
export const USER_UPDATE_DEFAULT_PER_MINUTE = 200_000;
/** Reason of withdrawn cards (`card_withdrawn{reason}`) when the action names none. */
export const USER_UPDATE_WITHDRAW_REASON = 'canvas_user_update';

/** A custom attribute of the registry; standard attributes (`$email`…) are profile data that Canvas never changes. */
const CanvasAttributeKey = z
  .string()
  .min(1)
  .max(255)
  .refine((k) => !k.startsWith('$'), 'standard attributes cannot be changed by a Canvas')
  .refine((k) => !k.includes('.'), 'attribute keys have no dots');

export const UserUpdateAction = z.discriminatedUnion('kind', [
  /** The value is Liquid (or a plain literal) converted to the attribute's registry type. */
  z.strictObject({ kind: z.literal('set_attribute'), key: CanvasAttributeKey, valueExpr: z.string().min(1).max(USER_UPDATE_MAX_EXPRESSION) }),
  z.strictObject({ kind: z.literal('increment_attribute'), key: CanvasAttributeKey, by: z.number().finite().refine((n) => n !== 0, 'increment by a non-zero number') }),
  z.strictObject({ kind: z.literal('remove_attribute'), key: CanvasAttributeKey }),
  /** `propertiesExpr` renders to a JSON object (Liquid allowed); left out, the event has no properties. */
  z.strictObject({ kind: z.literal('log_event'), name: EventName, propertiesExpr: z.string().min(1).max(USER_UPDATE_MAX_EXPRESSION).optional() }),
  /** Withdraws the profile's cards (BB-65); an `extrasValue` may be Liquid (CP-03: `bonus-campaign` of the entry's event). */
  z.strictObject({ kind: z.literal('withdraw_cards'), selector: CardWithdrawSelector, reason: CardWithdrawReason.optional() }),
]);
export type UserUpdateAction = z.infer<typeof UserUpdateAction>;
export const USER_UPDATE_ACTION_KINDS = ['set_attribute', 'increment_attribute', 'remove_attribute', 'log_event', 'withdraw_cards'] as const;

/**
 * User Update step settings (BB-120, CAN-010): up to 10 typed actions instead of Braze's free JSON — attributes of the
 * registry that a Canvas may write (no trusted or PII ones), events of the registry, card withdrawal. An attribute
 * takes at most one action, so the order never changes the result.
 */
export const UserUpdateConfig = z
  .strictObject({ actions: z.array(UserUpdateAction).min(1).max(USER_UPDATE_MAX_ACTIONS) })
  .superRefine((c, ctx) => {
    const seen = new Set<string>();
    c.actions.forEach((a, i) => {
      if (a.kind !== 'set_attribute' && a.kind !== 'increment_attribute' && a.kind !== 'remove_attribute') return;
      if (seen.has(a.key)) ctx.addIssue({ code: 'custom', message: `${a.key} already has an action in this step`, path: ['actions', i, 'key'] });
      seen.add(a.key);
    });
  });
export type UserUpdateConfig = z.infer<typeof UserUpdateConfig>;

/** The only way out of a User Update step. */
export const USER_UPDATE_NEXT = 'next';
